import React, { useEffect, useState } from 'react'
import classNames from 'classnames'
import { Maybe } from 'graphql/jsutils/Maybe'
import { When } from 'react-if'
import LazyHydrate from 'react-lazy-hydration'
import { ResponsiveFlexRow } from '@/atoms/FlexContainers'
import { AsSpan, CaptionXS, HeadingXS, TitleLG, TitleXS } from '@/atoms/Text'
import { PRICE_ANCHOR_EXPERIMENT_VARIANTS } from '@/constants/guild'
import { paths } from '@/constants/paths'
import { slugs } from '@/constants/slugs'
import { useLanding } from '@/contexts/LandingContext'
import { CollectionRail } from '@/organisms/CollectionRail'
import { InvestmentBanner } from '@/organisms/InvestmentBanner'
import { AngelInvestmentBanner } from '@/organisms/InvestmentBanner/AngelInvestmentBanner'
import { getContentfulClient, getWebClient } from '@/services/ApolloClient'
import { InvestmentBannerResult } from '@/services/CmsService/InvestmentBanner'
import { contentfulFaqLocations, useProjectFaqs } from '@/services/FaqService'
import { useGuildMember } from '@/services/GuildUserService'
import { getUserGuildDiscounts } from '@/services/Theatrical/queries'
import { useUser } from '@/services/UserService'
import { TheatricalDiscount } from '@/types/codegen-federation'
import { useLocale } from '@/utils/LocaleUtil'
import { logger } from '@/utils/logging'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { AppPromoSection } from '@/views/LandingView/AppPromoSection'
import { CreatePromoSection } from '@/views/LandingView/CreatePromoSection'
import { OmniGuildPlanSelectionCard } from '../GuildJoinView/OmniGuildPlanSelectionCard'
import { GuildNowStreamingRail } from '../GuildSignupView/GuildBodySection/GuildNowStreamingRail'
import { GuildUpcomingRail } from '../GuildSignupView/GuildBodySection/GuildUpcomingRail'
import { GuildTicketsCalloutSection } from '../GuildSignupView/GuildTicketsCalloutSection'
import { CurrentReleases } from '../TicketsLandingView/NewTicketsLandingView/components/CurrentReleases'
import { extractCurrentReleases } from '../TicketsLandingView/NewTicketsLandingView/utils'
import { FaqSection } from './FaqSection'
import { MainHeroSection } from './MainHeroSection'

const paddingBlendY = 'py-12 md:py-[5rem] xl:py-[6rem]'

interface NewLandingViewProps {
  investmentBanner: Maybe<InvestmentBannerResult>
  preview: boolean
}

export const NewLandingView: React.FC<NewLandingViewProps> = ({ investmentBanner, preview }) => {
  const { locale } = useLocale()
  const [guildDiscounts, setGuildDiscounts] = useState<TheatricalDiscount[]>([])
  const { t } = useTranslate('home')
  const { user, userId, isLoggedIn } = useUser()
  const { sweepstakes, releases } = useLanding()
  const { isGuildMember, guildPermissions, loading } = useGuildMember()

  useEffect(() => {
    let cancelled = false

    const loadDiscounts = async () => {
      if (!userId) {
        setGuildDiscounts([])
        return
      }
      const client = getWebClient({ locale })
      try {
        const discounts = await getUserGuildDiscounts(client, userId)
        if (!cancelled) setGuildDiscounts(discounts ?? [])
      } catch (error) {
        logger().error('Failed to load user guild discounts', { user }, error)
        if (!cancelled) setGuildDiscounts([])
      }
    }

    loadDiscounts()
    return () => {
      cancelled = true
    }
  }, [locale, user, userId])

  const isPremiumGuildUser = !!isGuildMember && !!guildPermissions?.hasFreeTickets

  const currentReleases = extractCurrentReleases({
    releases: releases ?? [],
    sweepstakes: sweepstakes ?? [],
    guildDiscounts: guildDiscounts,
  })

  const hasMovieTickets = guildDiscounts.length > 0

  const { faqs, loadingProjectFaqs } = useProjectFaqs({
    locale,
    preview,
    projectSlug: slugs.angelStudios,
    location: contentfulFaqLocations.home,
  })

  const client = getContentfulClient({ locale, preview })

  return (
    <div className="mx-auto flex flex-col bg-core-gray-950 text-white">
      <MainHeroSection
        client={client}
        isLoggedIn={isLoggedIn}
        loading={false}
        preview={preview}
        isGuildMember={isGuildMember}
        guildPermissions={guildPermissions}
        newVersion
      />
      <When condition={!isPremiumGuildUser}>
        <TitleLG
          weight="bold"
          color="white"
          className="xl:photon-heading-md py-8 text-center capitalize lg:pb-14 lg:pt-16"
        >
          <Translate i18nKey="supportFilmmakersCancelAnytime" t={t}>
            Support films & filmmakers that share your values. Cancel anytime.
          </Translate>
        </TitleLG>
        <ResponsiveFlexRow className="mx-auto flex w-full max-w-[1200px] gap-4 md:!items-stretch md:gap-8">
          <OmniGuildPlanSelectionCard
            className="mt-0"
            darkMode={false}
            hideLabels
            isSignupLayout={false}
            guildOfferPackage={PRICE_ANCHOR_EXPERIMENT_VARIANTS['18-169 / 20-179']}
            defaultSelectedOffer={'basic'}
          />
        </ResponsiveFlexRow>
        <div className="w-full items-center justify-center px-4 pt-2 text-center md:pt-6">
          <CaptionXS weight="medium" className="md:photon-caption-sm">
            <Translate t={t} i18nKey="fullHdUltraUdAvailabilityDisclaimer">
              Full HD (1080p) and Ultra HD (4K) availability are subject to your internet service and device
              capabilities. Not all content is available in all resolutions.
            </Translate>
          </CaptionXS>
        </div>
        <div className="w-full items-center justify-center px-4 pb-10 pt-2 text-center md:pb-[138px]">
          <CaptionXS weight="medium" className="md:photon-caption-sm">
            <Translate t={t} i18nKey="notInterestedPaidJoinFreeShowsFilms">
              Not interested in a paid membership? - Click{' '}
              <a href={paths.watch.index} className="underline">
                here
              </a>{' '}
              to watch our free shows & films
            </Translate>
          </CaptionXS>
        </div>
      </When>
      <div className="relative h-full w-full">
        <div className="absolute inset-0 h-full w-full overflow-y-auto overflow-x-hidden bg-[url('https://images.angelstudios.com/image/upload/q_auto:eco/v1689354516/angel-app/assets/discover/background_blur_v2.webp')] bg-cover" />
        {/* Top -> Bottom Gradient */}
        <div
          className={classNames('absolute inset-0 left-0 top-0 h-full w-full')}
          style={{
            background: 'linear-gradient(180deg, #141414 0%, rgba(21, 21, 21, 0) 40%)',
          }}
        />
        {/* Bottom -> Top Gradient  */}
        <div
          className={classNames('absolute inset-0 left-0 bottom-0 h-full w-full')}
          style={{
            background: 'linear-gradient(0deg, #141414 18.15%, rgba(21, 21, 21, 0) 40%)',
          }}
        />
        <div className="pt-[104px]">
          <div className="relative grid grid-cols-12">
            <HeadingXS
              as={AsSpan}
              weight="bold"
              color="white"
              className="lg:photon-display col-span-12 col-start-1 col-end-13 mx-auto mb-4 self-center px-4 text-center lg:col-span-8 lg:col-start-3 lg:mb-8 lg:max-w-[900px] se:max-w-[350px] se:text-2xl"
            >
              <Translate i18nKey="moviesAndShowsYoullLoveCapitalized" t={t}>
                We&apos;re Curating a Library of Shows You&apos;ll Love.
              </Translate>
            </HeadingXS>
            <TitleXS
              as={AsSpan}
              weight="semibold"
              color="core-gray-400"
              className="lg:photon-title-lg col-span-10 col-start-2 col-end-12 mx-auto mb-16 max-w-[1000px] self-center text-center lg:col-span-8 lg:col-start-3 lg:mb-16"
            >
              <Translate i18nKey="launchingNewTitlesEveryWeekV1" t={t}>
                We plan to launch more titles every week in 2025, thanks to Guild members like you.
              </Translate>
            </TitleXS>
          </div>
        </div>
        <div className="pb-[104px]">
          <section className="relative mb-12 h-full overflow-x-hidden px-4 scrollbar-none sm:px-8 md:px-12 xl:px-16">
            <GuildNowStreamingRail landing />
          </section>
          <section className="relative mb-12 h-full overflow-x-hidden px-4 scrollbar-none sm:px-8 md:px-12 xl:px-16">
            <CollectionRail
              landing
              title={t('top10MoviesGuildMembersTalkingAbout', 'Top 10 Movies Guild Members are Talking About')}
              collectionId="88326055-54dc-48ab-ac44-89d26727d908"
              isGuildMember={isGuildMember}
              loading={loading}
              showNumberedRail
            />
          </section>
          <section className="relative mb-12 h-full overflow-x-hidden px-4 scrollbar-none sm:px-8 md:px-12 xl:px-16">
            <GuildUpcomingRail landing />
          </section>
        </div>
      </div>
      <div className="w-full overflow-x-hidden">
        <CurrentReleases
          releases={currentReleases}
          className={hasMovieTickets ? paddingBlendY : 'pb-[48px] pt-[64px] md:pb-[96px] md:pt-[120px]'}
          addAnimation={hasMovieTickets}
          source={'landing'}
          containerClassName="!px-4 sm:!px-8 md:!px-12 xl:!px-16"
        />
      </div>
      <LazyHydrate whenVisible>
        <div className="mb-20 mt-40 bg-core-gray-950">
          <GuildTicketsCalloutSection landing isPremiumGuildUser={isPremiumGuildUser} />
        </div>
      </LazyHydrate>
      <AngelInvestmentBanner />
      {investmentBanner && <InvestmentBanner {...investmentBanner} className="my-6 md:my-8 lg:my-12" />}
      <CreatePromoSection newVersion />
      <div className="bg-core-gray-950 pt-4">
        <AppPromoSection />
      </div>

      {!loadingProjectFaqs && <FaqSection faqs={faqs} />}
    </div>
  )
}

export const defaultGuildTestimonials = {
  __typename: 'GuildTestimonialSection',
  testimonialsCollection: {
    __typename: 'GuildTestimonialSectionTestimonialsCollection',
    items: [
      {
        __typename: 'Critics',
        _id: '1',
        author: 'Jennifer, Facebook Comment',
        company: null,
        rating: null,
        quote:
          "ANGEL IS LIKE NO OTHER it's so good I'm blown away!!! And we the people get to help decide! I'm speechless at how amazing it is- I've canceled all other streaming apps! Get it - you won't be sorry!!",
        contentfulMetadata: {
          tags: [],
          concepts: [],
        },
        sys: {
          id: '1',
          publishedAt: null,
          firstPublishedAt: null,
          publishedVersion: null,
          environmentId: '',
          spaceId: '',
        },
      },
      {
        __typename: 'Critics',
        _id: '2',
        author: 'Debbie, Facebook Comment',
        company: null,
        rating: null,
        quote:
          'Not only do you get access to quality entertainment, you actually support the making of quality entertainment.',
        contentfulMetadata: {
          tags: [],
          concepts: [],
        },
        sys: {
          id: '2',
          publishedAt: null,
          firstPublishedAt: null,
          publishedVersion: null,
          environmentId: '',
          spaceId: '',
        },
      },
      {
        __typename: 'Critics',
        _id: '3',
        author: 'Stacy ',
        company: null,
        rating: null,
        quote:
          'In a world full of Hollywood slop and smut, it is an honor to support uplifting, meaningful and excellently done media!',
        contentfulMetadata: {
          tags: [],
          concepts: [],
        },
        sys: {
          id: '3',
          publishedAt: null,
          firstPublishedAt: null,
          publishedVersion: null,
          environmentId: '',
          spaceId: '',
        },
      },
      {
        __typename: 'Critics',
        _id: '4',
        author: 'Robin, Facebook comment ',
        company: null,
        rating: null,
        quote:
          "Great movies and such a great cause to support someone making family friendly movies! Thank you Angel Studios!! You can't do it for free and what you charge is well worth it.",
        contentfulMetadata: {
          tags: [],
          concepts: [],
        },
        sys: {
          id: '4',
          publishedAt: null,
          firstPublishedAt: null,
          publishedVersion: null,
          environmentId: '',
          spaceId: '',
        },
      },
      {
        __typename: 'Critics',
        _id: '5',
        author: 'Martha, Facebook Comment',
        company: null,
        rating: null,
        quote:
          'I too, am a Guild member. I love it Angel Studios. I enjoy the movies that are encouraging and many are based on true stories.',
        contentfulMetadata: {
          tags: [],
          concepts: [],
        },
        sys: {
          id: '5',
          publishedAt: null,
          firstPublishedAt: null,
          publishedVersion: null,
          environmentId: '',
          spaceId: '',
        },
      },
    ],
    limit: 0,
    skip: 0,
    total: 0,
  },
  cinemaScore: null,
  tomatometer: null,
  audience: null,
  desktopBackgroundImageUrl: null,
  tabletBackgroundImageUrl: null,
  mobileBackgroundImageUrl: null,
  guildSpecific: true,
  contentfulMetadata: {
    tags: [],
    concepts: [],
  },
  sys: {
    id: '5',
    publishedAt: null,
    firstPublishedAt: null,
    publishedVersion: null,
    environmentId: '',
    spaceId: '',
  },
  _id: '1',
}

export const defaultTicketSection = {
  __typename: 'GuildSignupTicketsSection',
  description: {
    json: {
      nodeType: 'document',
      data: {},
      content: [
        {
          nodeType: 'paragraph',
          data: {},
          content: [
            {
              nodeType: 'text',
              value: 'Premium members get 2 movie tickets to all Angel theatrical releases.',
              marks: [],
              data: {},
            },
          ],
        },
      ],
    },
  },
  ticketsImageUrl: '/v1743380525/guild-tickets-graphic_4x_vy1pkn.webp',
  title: 'Premium Members Get Tickets',
  sys: {
    id: '5',
    publishedAt: null,
    firstPublishedAt: null,
    publishedVersion: null,
    environmentId: '',
    spaceId: '',
  },
}
